import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (item: T, index: number) => ReactNode;
  className?: string;
  headerClassName?: string;
}

interface ReferralTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  emptyMessage?: string;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
}

export function ReferralTable<T extends { id: string }>({ 
  data, 
  columns, 
  emptyMessage = "No data found",
  className = "",
  headerClassName = "",
  rowClassName = ""
}: ReferralTableProps<T>) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-500 space-grotesk">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-slate-200">
        <thead className={`bg-slate-50 ${headerClassName}`}>
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key}
                className={`px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk ${column.headerClassName || ''}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {data.map((item, index) => (
            <tr key={item.id} className={`hover:bg-slate-50 ${rowClassName}`}>
              {columns.map((column) => (
                <td 
                  key={column.key}
                  className={`px-6 py-4 whitespace-nowrap ${column.className || ''}`}
                >
                  {column.render(item, index)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper components for common table cell types
export const TableCellAvatar = ({ 
  src, 
  fallback, 
  name, 
  className = "" 
}: { 
  src?: string; 
  fallback: string; 
  name: string; 
  className?: string; 
}) => (
  <div className={`flex items-center ${className}`}>
    <Avatar className="h-8 w-8 mr-3">
      <AvatarImage src={src} />
      <AvatarFallback className="bg-slate-300 text-slate-700 text-xs">
        {fallback}
      </AvatarFallback>
    </Avatar>
    <div className="text-sm font-medium text-slate-900 space-grotesk">
      {name}
    </div>
  </div>
);

export const TableCellBadge = ({ 
  text, 
  className = "" 
}: { 
  text: string; 
  className?: string; 
}) => (
  <Badge className={className}>
    {text}
  </Badge>
);

export const TableCellText = ({ 
  text, 
  className = "text-sm text-slate-900 space-grotesk" 
}: { 
  text: string; 
  className?: string; 
}) => (
  <span className={className}>
    {text}
  </span>
);

export const TableCellDate = ({ 
  date, 
  className = "text-sm text-slate-500 space-grotesk" 
}: { 
  date: string | Date; 
  className?: string; 
}) => {
  return (
    <span className={className}>
      {formatDateForSouthAfrica(date)}
    </span>
  );
};

export const TableCellAmount = ({ 
  amount, 
  isHighlighted = false,
  className = "" 
}: { 
  amount: string; 
  isHighlighted?: boolean;
  className?: string; 
}) => (
  <span className={`text-sm font-medium space-grotesk ${
    isHighlighted ? "text-green-600" : "text-slate-500"
  } ${className}`}>
    {amount === "-" ? amount : `R${formatRewardAmount(amount)}`}
  </span>
);